import React, { useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { documentsAPI } from "../../../services/api";
import "./AdminSections.css";

const inferContentTypeFromFile = (fileName = "") => {
  const lower = fileName.toLowerCase();
  if (lower.endsWith(".html") || lower.endsWith(".htm")) return "html";
  return "markdown";
};

const stripExtension = (fileName = "") => {
  const idx = fileName.lastIndexOf(".");
  if (idx <= 0) return fileName;
  return fileName.slice(0, idx);
};

const formatDate = (d) => {
  const t = new Date(d);
  if (Number.isNaN(t.getTime())) return "";
  return t.toLocaleString();
};

const isHtml = (contentType = "") => contentType.toLowerCase().includes("html");

function ContentManagement() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [docs, setDocs] = useState([]);
  const [categories, setCategories] = useState([]);

  const [message, setMessage] = useState(null); // {type:'success'|'error', text:string}

  // Upload form state
  const [category, setCategory] = useState("");
  const [name, setName] = useState("");
  const [isPublicDoc, setIsPublicDoc] = useState(true);
  const [sortOrder, setSortOrder] = useState(0);

  const [contentType, setContentType] = useState("markdown");
  const [content, setContent] = useState("");
  const [fileInfo, setFileInfo] = useState(null);

  // List controls
  const [filterCategory, setFilterCategory] = useState("ALL");
  const [search, setSearch] = useState("");
  const [showPrivate, setShowPrivate] = useState(true);

  // Preview
  const [previewId, setPreviewId] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Edit
  const [editId, setEditId] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editForm, setEditForm] = useState({
    category: "",
    name: "",
    contentType: "markdown",
    content: "",
    sortOrder: 0,
    isPublic: true,
  });

  // Rename category tool
  const [renameOld, setRenameOld] = useState("");
  const [renameNew, setRenameNew] = useState("");
  const [renaming, setRenaming] = useState(false);

  const loadAll = async () => {
    try {
      setLoading(true);
      setMessage(null);

      const [docsRes, catsRes] = await Promise.all([
        documentsAPI.adminList(),
        documentsAPI.adminGetCategories(),
      ]);

      setDocs(docsRes.data || []);
      // ✅ FIXED: missing closing parenthesis
      setCategories((catsRes.data || []).map((c) => c.category).filter(Boolean));
    } catch (e) {
      console.error(e);
      setMessage({ type: "error", text: "Failed to load documents." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredDocs = useMemo(() => {
    const q = search.trim().toLowerCase();

    return (docs || [])
      .filter((d) => (showPrivate ? true : !!d.isPublic))
      .filter((d) => (filterCategory === "ALL" ? true : d.category === filterCategory))
      .filter((d) => (q ? (d.name || "").toLowerCase().includes(q) : true))
      .sort((a, b) => {
        const ac = (a.category || "").localeCompare(b.category || "");
        if (ac !== 0) return ac;
        const ao = (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
        if (ao !== 0) return ao;
        return (a.name || "").localeCompare(b.name || "");
      });
  }, [docs, filterCategory, search, showPrivate]);

  const handlePickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const inferredType = inferContentTypeFromFile(file.name);
      const inferredName = stripExtension(file.name);

      setFileInfo({ name: file.name, size: file.size });
      setContent(text);
      setContentType(inferredType);

      if (!name) setName(inferredName);
    } catch (err) {
      console.error(err);
      setMessage({ type: "error", text: "Failed to read file." });
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    setMessage(null);

    if (!category.trim()) return setMessage({ type: "error", text: "Category is required." });
    if (!name.trim()) return setMessage({ type: "error", text: "Name is required." });

    try {
      setSaving(true);

      await documentsAPI.adminCreate({
        category: category.trim(),
        name: name.trim(),
        content,
        contentType,
        isPublic: isPublicDoc,
        sortOrder: Number(sortOrder) || 0,
      });

      setMessage({ type: "success", text: "Document uploaded." });

      // reset form (keep category to make batch uploads easy)
      setName("");
      setContent("");
      setFileInfo(null);
      setContentType("markdown");
      setIsPublicDoc(true);
      setSortOrder(0);

      await loadAll();
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.error || "Upload failed.";
      setMessage({ type: "error", text: msg });
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePublic = async (docRow) => {
    try {
      setMessage(null);
      const updated = await documentsAPI.adminUpdate(docRow.id, { isPublic: !docRow.isPublic });
      setDocs((prev) =>
        (prev || []).map((d) => (d.id === docRow.id ? { ...d, ...updated.data } : d))
      );

      if (editId === docRow.id) {
        setEditForm((f) => ({ ...f, isPublic: !docRow.isPublic }));
      }
    } catch (err) {
      console.error(err);
      setMessage({ type: "error", text: "Failed to update visibility." });
    }
  };

  const handleDelete = async (docRow) => {
    const ok = window.confirm(`Delete "${docRow.name}"? This cannot be undone.`);
    if (!ok) return;

    try {
      setMessage(null);
      await documentsAPI.adminDelete(docRow.id);
      setDocs((prev) => (prev || []).filter((d) => d.id !== docRow.id));

      if (previewId === docRow.id) {
        setPreviewId(null);
        setPreviewDoc(null);
      }
      if (editId === docRow.id) {
        setEditId(null);
        setEditForm({
          category: "",
          name: "",
          contentType: "markdown",
          content: "",
          sortOrder: 0,
          isPublic: true,
        });
      }
    } catch (err) {
      console.error(err);
      setMessage({ type: "error", text: "Failed to delete document." });
    }
  };

  const handlePreview = async (docRow) => {
    try {
      setPreviewId(docRow.id);
      setPreviewDoc(null);
      setPreviewLoading(true);

      const res = await documentsAPI.adminGetDocument(docRow.id);
      setPreviewDoc(res.data || null);
    } catch (err) {
      console.error(err);
      setMessage({ type: "error", text: "Failed to load preview." });
    } finally {
      setPreviewLoading(false);
    }
  };

  const startEdit = async (docRow) => {
    try {
      setEditId(docRow.id);
      setEditLoading(true);
      setMessage(null);

      const res = await documentsAPI.adminGetDocument(docRow.id);
      const d = res.data;

      setEditForm({
        category: d.category || "",
        name: d.name || "",
        contentType: d.contentType || "markdown",
        content: d.content || "",
        sortOrder: Number.isFinite(Number(d.sortOrder)) ? Number(d.sortOrder) : 0,
        isPublic: !!d.isPublic,
      });
    } catch (err) {
      console.error(err);
      setMessage({ type: "error", text: "Failed to load document for editing." });
    } finally {
      setEditLoading(false);
    }
  };

  const saveEdit = async () => {
    if (!editId) return;

    const payload = {
      category: editForm.category?.trim(),
      name: editForm.name?.trim(),
      contentType: editForm.contentType,
      content: editForm.content,
      sortOrder: Number(editForm.sortOrder) || 0,
      isPublic: !!editForm.isPublic,
    };

    if (!payload.category) return setMessage({ type: "error", text: "Edit: category is required." });
    if (!payload.name) return setMessage({ type: "error", text: "Edit: name is required." });

    try {
      setEditSaving(true);
      setMessage(null);

      const res = await documentsAPI.adminUpdate(editId, payload);

      setDocs((prev) => (prev || []).map((d) => (d.id === editId ? { ...d, ...res.data } : d)));

      setMessage({ type: "success", text: "Document saved." });
      await loadAll();
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.error || "Failed to save document.";
      setMessage({ type: "error", text: msg });
    } finally {
      setEditSaving(false);
    }
  };

  const renameCategory = async () => {
    const oldCategory = (renameOld || "").trim();
    const newCategory = (renameNew || "").trim();

    if (!oldCategory) return setMessage({ type: "error", text: "Pick a category to rename." });
    if (!newCategory) return setMessage({ type: "error", text: "Enter the new category name." });

    const ok = window.confirm(
      `Rename category "${oldCategory}" → "${newCategory}"?\nThis changes ALL documents in that category.`
    );
    if (!ok) return;

    try {
      setRenaming(true);
      setMessage(null);

      const res = await documentsAPI.adminRenameCategory(oldCategory, newCategory);

      setMessage({
        type: "success",
        text: `Renamed category. Updated ${res?.data?.affected ?? 0} documents.`,
      });

      setRenameOld("");
      setRenameNew("");
      await loadAll();

      if (editId && editForm.category === oldCategory) {
        setEditForm((f) => ({ ...f, category: newCategory }));
      }
      if (filterCategory === oldCategory) setFilterCategory(newCategory);
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.error || "Failed to rename category.";
      setMessage({ type: "error", text: msg });
    } finally {
      setRenaming(false);
    }
  };

  const previewIsHtml = isHtml(previewDoc?.contentType || "");
  const editIsHtml = isHtml(editForm.contentType || "");

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2>📚 Content Management</h2>
        <p className="section-subtitle">Upload and manage source materials</p>
      </div>

      {message?.type === "success" ? <div className="success-message">{message.text}</div> : null}
      {message?.type === "error" ? <div className="error-message">{message.text}</div> : null}

      {/* Category rename tool */}
      <div className="admin-form-container">
        <h3>Rename Category</h3>
        <div className="admin-form" style={{ gap: "1rem" }}>
          <div className="form-group">
            <label>Existing Category</label>
            <select value={renameOld} onChange={(e) => setRenameOld(e.target.value)}>
              <option value="">Select…</option>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>New Category Name</label>
            <input
              type="text"
              value={renameNew}
              onChange={(e) => setRenameNew(e.target.value)}
              placeholder="Enter new category name"
            />
          </div>

          <div className="form-actions" style={{ marginTop: 0 }}>
            <button className="btn-primary" type="button" onClick={renameCategory} disabled={renaming}>
              {renaming ? "Renaming…" : "Rename Category"}
            </button>
            <button
              className="btn-secondary"
              type="button"
              onClick={() => {
                setRenameOld("");
                setRenameNew("");
              }}
              disabled={renaming}
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Upload */}
      <div className="admin-form-container">
        <h3>Upload Document</h3>

        <form className="admin-form" onSubmit={handleUpload}>
          <div className="form-group">
            <label>Category</label>
            <input
              type="text"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              list="doc-categories"
              placeholder="e.g. Rules, Lore, GM Resources"
            />
            <datalist id="doc-categories">
              {categories.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
            <div className="form-hint">Type a new one or pick an existing category.</div>
          </div>

          <div className="form-group">
            <label>Name</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Document name" />
          </div>

          <div className="form-group">
            <label>File</label>
            <input type="file" accept=".md,.markdown,.txt,.html,.htm" onChange={handlePickFile} />
            {fileInfo ? (
              <div className="form-hint">
                Selected: {fileInfo.name} ({Math.round(fileInfo.size / 1024)} KB) — inferred type: {contentType}
              </div>
            ) : (
              <div className="form-hint">Upload Markdown or HTML. We store file contents in the DB.</div>
            )}
          </div>

          <div className="form-group">
            <label>Content (optional edit)</label>
            <textarea value={content} onChange={(e) => setContent(e.target.value)} placeholder="Content will populate after choosing a file…" />
          </div>

          <div className="form-group">
            <label>Sort Order</label>
            <input type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} />
            <div className="form-hint">Lower numbers appear first within a category.</div>
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input type="checkbox" checked={isPublicDoc} onChange={(e) => setIsPublicDoc(e.target.checked)} />
              <span>Public (visible to players)</span>
            </label>
          </div>

          <div className="form-actions">
            <button className="btn-primary" type="submit" disabled={saving}>
              {saving ? "Uploading…" : "Upload"}
            </button>
            <button
              className="btn-secondary"
              type="button"
              onClick={() => {
                setName("");
                setContent("");
                setFileInfo(null);
                setContentType("markdown");
                setIsPublicDoc(true);
                setSortOrder(0);
                setMessage(null);
              }}
              disabled={saving}
            >
              Clear
            </button>
          </div>
        </form>
      </div>

      {/* List */}
      <div className="admin-data-list">
        <h3>Documents</h3>

        <div className="admin-form-container" style={{ marginBottom: "1.5rem" }}>
          <div className="admin-form" style={{ gap: "1rem" }}>
            <div className="form-group">
              <label>Filter Category</label>
              <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
                <option value="ALL">All</option>
                {categories.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Search</label>
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name…" />
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input type="checkbox" checked={showPrivate} onChange={(e) => setShowPrivate(e.target.checked)} />
                <span>Show private docs</span>
              </label>
            </div>

            <div className="form-actions" style={{ marginTop: 0 }}>
              <button className="btn-secondary" type="button" onClick={loadAll} disabled={loading}>
                Refresh
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="empty-state">Loading…</div>
        ) : filteredDocs.length === 0 ? (
          <div className="empty-state">No documents found.</div>
        ) : (
          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Category</th>
                  <th>Public</th>
                  <th>Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDocs.map((d) => (
                  <tr key={d.id}>
                    <td className="title-cell">{d.name}</td>
                    <td>
                      <span className="category-badge badge-update">{d.category}</span>
                    </td>
                    <td>{d.isPublic ? "Yes" : "No"}</td>
                    <td>{formatDate(d.lastUpdated)}</td>
                    <td className="actions-cell">
                      <button className="btn-view" type="button" onClick={() => handlePreview(d)} title="Preview">
                        👁️
                      </button>
                      <button className="btn-edit" type="button" onClick={() => startEdit(d)} title="Edit">
                        ✏️
                      </button>
                      <button className="btn-edit" type="button" onClick={() => handleTogglePublic(d)} title="Toggle Public">
                        {d.isPublic ? "🔒" : "🌐"}
                      </button>
                      <button className="btn-delete" type="button" onClick={() => handleDelete(d)} title="Delete">
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit */}
      {editId ? (
        <div className="admin-form-container" style={{ marginTop: "2rem" }}>
          <h3>Edit Document</h3>

          {editLoading ? (
            <div className="empty-state">Loading editor…</div>
          ) : (
            <>
              <div className="admin-form" style={{ gap: "1rem" }}>
                <div className="form-group">
                  <label>Category</label>
                  <input
                    type="text"
                    value={editForm.category}
                    onChange={(e) => setEditForm((f) => ({ ...f, category: e.target.value }))}
                    list="doc-categories"
                  />
                </div>

                <div className="form-group">
                  <label>Name</label>
                  <input type="text" value={editForm.name} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} />
                </div>

                <div className="form-group">
                  <label>Content Type</label>
                  <select value={editForm.contentType} onChange={(e) => setEditForm((f) => ({ ...f, contentType: e.target.value }))}>
                    <option value="markdown">markdown</option>
                    <option value="html">html</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Sort Order</label>
                  <input type="number" value={editForm.sortOrder} onChange={(e) => setEditForm((f) => ({ ...f, sortOrder: e.target.value }))} />
                </div>

                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={!!editForm.isPublic}
                      onChange={(e) => setEditForm((f) => ({ ...f, isPublic: e.target.checked }))}
                    />
                    <span>Public</span>
                  </label>
                </div>

                <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                  <label>Content</label>
                  <textarea value={editForm.content} onChange={(e) => setEditForm((f) => ({ ...f, content: e.target.value }))} style={{ minHeight: 220 }} />
                </div>

                <div className="form-actions" style={{ gridColumn: "1 / -1" }}>
                  <button className="btn-primary" type="button" onClick={saveEdit} disabled={editSaving}>
                    {editSaving ? "Saving…" : "Save"}
                  </button>
                  <button
                    className="btn-secondary"
                    type="button"
                    onClick={() => {
                      setEditId(null);
                      setEditForm({
                        category: "",
                        name: "",
                        contentType: "markdown",
                        content: "",
                        sortOrder: 0,
                        isPublic: true,
                      });
                    }}
                    disabled={editSaving}
                  >
                    Close
                  </button>
                </div>
              </div>

              <div style={{ marginTop: "1rem" }}>
                <h4 style={{ marginBottom: 10, color: "#c5c5d8" }}>Live Preview</h4>
                {editIsHtml ? (
                  <iframe
                    title="Edit Preview"
                    srcDoc={editForm.content || ""}
                    style={{
                      width: "100%",
                      height: "420px",
                      border: "1px solid #312378",
                      borderRadius: 8,
                      background: "#0f0f14",
                    }}
                    sandbox="allow-same-origin allow-scripts allow-forms allow-modals"
                  />
                ) : (
                  <div
                    style={{
                      padding: "1rem",
                      border: "1px solid #312378",
                      borderRadius: 8,
                      background: "rgba(10,10,15,0.6)",
                      color: "#c5c5d8",
                    }}
                  >
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{editForm.content || ""}</ReactMarkdown>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      ) : null}

      {/* Preview */}
      {previewId ? (
        <div className="admin-form-container" style={{ marginTop: "2rem" }}>
          <h3>Preview</h3>

          {previewLoading ? (
            <div className="empty-state">Loading preview…</div>
          ) : !previewDoc ? (
            <div className="empty-state">Select a document to preview.</div>
          ) : (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", gap: "1rem", alignItems: "center" }}>
                <div style={{ color: "#c5c5d8" }}>
                  <strong style={{ color: "#9D7FEA" }}>{previewDoc.name}</strong>
                  <div style={{ opacity: 0.8, marginTop: 4 }}>
                    {previewDoc.category} • {previewDoc.isPublic ? "Public" : "Private"} • {previewDoc.contentType}
                  </div>
                </div>

                <button className="btn-secondary" type="button" onClick={() => { setPreviewId(null); setPreviewDoc(null); }}>
                  Close Preview
                </button>
              </div>

              <div style={{ marginTop: "1rem" }}>
                {previewIsHtml ? (
                  <iframe
                    title={previewDoc.name}
                    srcDoc={previewDoc.content || ""}
                    style={{
                      width: "100%",
                      height: "500px",
                      border: "1px solid #312378",
                      borderRadius: 8,
                      background: "#0f0f14",
                    }}
                    sandbox="allow-same-origin allow-scripts allow-forms allow-modals"
                  />
                ) : (
                  <div
                    style={{
                      padding: "1rem",
                      border: "1px solid #312378",
                      borderRadius: 8,
                      background: "rgba(10,10,15,0.6)",
                      color: "#c5c5d8",
                    }}
                  >
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{previewDoc.content || ""}</ReactMarkdown>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

export default ContentManagement;
